import { useAppSelector } from './redux';
import { selected as selectedOrganisation } from '@modules/organisation/selectors/organisation';
import { selected as selectedNetwork } from '@modules/network/selectors/network';
import { EComponentTypes } from '@common/definitions';

type ComponentTypesKeys = keyof typeof EComponentTypes;
type ComponentTypesValues = typeof EComponentTypes[ComponentTypesKeys];

interface UseComponentVisibilityResult {
  isVisibleOnOrg: boolean;
  isVisibleOnNetwork: boolean;
}

type UseComponentVisibilityOptions = {
  orgCheck?: 'enabled_only' | 'available_or_enabled' | 'optional';
};

/**
 * Hook to check component visibility based on organisation and network enabled_components.
 *
 * orgCheck:
 * - enabled_only: org from enabled_components only; network visibility requires org AND network.
 * - available_or_enabled: org from available or enabled lists; network visibility requires org AND network.
 * - optional: same org resolution as available_or_enabled; network visibility is org OR network
 *   (org lists can lag; network can still show the feature if enabled on that network).
 */
export function useComponentVisibility(
  component: ComponentTypesValues,
  options?: UseComponentVisibilityOptions
): UseComponentVisibilityResult {
  const organisation = useAppSelector(selectedOrganisation);
  const network = useAppSelector(selectedNetwork);

  const orgCheckMode = options?.orgCheck ?? 'enabled_only';
  const orgHasComponent =
    orgCheckMode === 'enabled_only'
      ? (organisation?.enabled_components?.includes(component) ?? false)
      : (
        organisation?.available_components?.includes(component) ||
        organisation?.enabled_components?.includes(component) ||
        false
      );
  const networkHasComponent = network?.enabled_components?.includes(component) ?? false;

  const isVisibleOnNetwork =
    orgCheckMode === 'optional'
      ? orgHasComponent || networkHasComponent
      : orgHasComponent && networkHasComponent;

  return {
    isVisibleOnOrg: orgHasComponent,
    isVisibleOnNetwork,
  };
}
